import React, { useState, useCallback } from 'react';
import Image from 'next/image';
import { Box, Button, IconAddInCircle24, IconEdit24, IconButton, IconCloseX16 } from '@stitch-fix/mode-react';
import { useSelectEvent } from '@stitch-fix/event-reporter';
import { useClientPhotoFullBodyCreateMutation, ClientPhotoFullBodyCreateMutation } from '../graphql/clientPhotoFullBodyCreate';
import { PhotoUpload, PhotoUploadRenderProps } from '../PhotoUpload/PhotoUpload';
import fullBodyPlaceholder from '../../../../../public/Onboarding_Full_Body_Placeholder.svg';
import PhotoUploadErrorModal from '../PhotoUploadErrorModal';
import { useDeleteClientPhotoMutation } from '../graphql/deleteClientPhoto';
import styles from './FullBodyUpload.module.scss';
export interface FullBodyUploadProps {
  id?: string;
  existingImageUrl?: string | null;
  onChange?: () => void;
}
export const FullBodyUpload: React.FC<FullBodyUploadProps> = ({
  id,
  existingImageUrl,
  onChange
}) => {
  const {
    reportSelectEvent
  } = useSelectEvent();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isImageUploadError, setIsImageUploadError] = useState(false);
  const [imageUploadErrorMessage, setImageUploadErrorMessage] = useState('');
  const [deleteFullBodyPhoto, {
    loading: isDeleting
  }] = useDeleteClientPhotoMutation();
  const [createFullBodyPhoto, {
    loading: fullBodyLoading
  }] = useClientPhotoFullBodyCreateMutation();
  const onDeleteClick = useCallback(() => {
    if (!id) {
      return;
    }
    deleteFullBodyPhoto({
      variables: {
        input: {
          id
        }
      },
      onCompleted: () => {
        onChange?.();
      }
    });
  }, [id, deleteFullBodyPhoto, onChange]);
  const handleUploadSuccess = useCallback((url: string) => {
    setIsProcessing(true);
    setIsUploading(false);
    createFullBodyPhoto({
      variables: {
        input: {
          url
        }
      },
      onCompleted: (data: ClientPhotoFullBodyCreateMutation) => {
        const result = data?.clientPhotoFullBodyCreate?.result;
        if (result?.__typename === 'ClientPhotoFullBodyCreateSuccess') {
          setIsProcessing(false);
          onChange?.();
          setIsImageUploadError(false);
          setImageUploadErrorMessage('');
        } else if (result?.__typename === 'ClientPhotoFullBodyCreateError') {
          setIsProcessing(false);
          setIsImageUploadError(true);
          setImageUploadErrorMessage(result.message || '');
        }
      },
      onError: () => {
        setIsProcessing(false);
      }
    });
  }, [createFullBodyPhoto, onChange]);
  const handleUploadClick = () => {
    setIsImageUploadError(false);
    reportSelectEvent({
      schema: 'select',
      name: 'update_full_body_image_button',
      action_name: 'update_full_body_image'
    });
    setIsUploading(true);
  };
  const renderFullBodyUpload = (props: PhotoUploadRenderProps) => {
    const {
      currentImageUrl,
      isButtonDisabled,
      buttonText,
      handleUploadClick: onUploadClick
    } = props;
    return <Box className={styles.container}>
        {currentImageUrl ? <div style={{
        width: '199px',
        height: '234px',
        borderRadius: '8px',
        overflow: 'visible',
        boxSizing: 'border-box',
        position: 'relative'
      }}>
            <img src={currentImageUrl} alt="Full body" style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          borderRadius: '8px'
        }} />
            <Box style={{
          position: 'absolute',
          top: '-20px',
          right: '-20px'
        }}>
              <IconButton variant="overlay" aria-label="Delete full body photo" onClick={onDeleteClick}>
                <IconCloseX16 />
              </IconButton>
            </Box>
          </div> : <Box className={styles.imageContainer}>
            <svg width="199" height="234" viewBox="0 0 199 234" fill="none" xmlns="http://www.w3.org/2000/svg" data-testid="rectangular-upload-area">
              <rect x="0.5" y="0.5" width="198" height="233" rx="7.5" fill="#F0F1F3" stroke="#001E60" strokeDasharray="7 7" />
            </svg>
            <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }}>
              <Image alt="" src={fullBodyPlaceholder} width={57} style={{
            width: '57',
            height: '107'
          }} />
            </div>
          </Box>}

        <Box style={{
        position: 'absolute',
        bottom: '-20px'
      }}>
          <Button startIcon={currentImageUrl ? <IconEdit24 /> : <IconAddInCircle24 />} onClick={onUploadClick} disabled={isButtonDisabled} variant="filled-staple" width="fit">
            {buttonText}
          </Button>
        </Box>
      </Box>;
  };
  return <PhotoUpload onUploadSuccess={handleUploadSuccess} onUploadError={() => setIsUploading(false)} initialUrl={existingImageUrl} isProcessing={isProcessing || fullBodyLoading || isDeleting} processingText={isDeleting ? 'Deleting...' : 'Loading...'} isUploading={isUploading} onUploadClick={handleUploadClick} onUploadCancel={() => setIsUploading(false)}>
      {props => <>
          {renderFullBodyUpload(props)}
          {isImageUploadError && <PhotoUploadErrorModal onDismiss={() => {
        props.discardStagedUpload();
        setIsImageUploadError(false);
        setImageUploadErrorMessage('');
      }} onPhotoUpload={() => {
        setIsImageUploadError(false);
        setImageUploadErrorMessage('');
        props.handleUploadClick();
      }} errorMessage={imageUploadErrorMessage} />}
        </>}
    </PhotoUpload>;
};